/**
 * Карточка — контейнер для блоков контента: в светлой теме белая
 * поверхность с мягкой тенью, в тёмной — тёмное матовое стекло со
 * светящейся кромкой (`.card-glow` в `index.css`). Поля 32–48px.
 *
 * Появление — мягкий подъём (`animate-slide-in`), при «меньше движения»
 * отключается.
 */

import { Paper } from '@mantine/core';
import type { ReactNode } from 'react';

interface CardProps {
  readonly children: ReactNode;
  /** Дополнительные классы раскладки — сетка, отступы, ширина. */
  readonly className?: string;
  /** Отключить анимацию появления (например, для карточки внутри списка). */
  readonly animate?: boolean;
  /** Обработчик клика делает карточку интерактивной и доступной с клавиатуры. */
  readonly onClick?: () => void;
}

export function Card({ children, className, animate = true, onClick }: CardProps) {
  const interactive = typeof onClick === 'function';

  return (
    <Paper
      radius="xl"
      shadow="md"
      className={[
        // Тонкая тёплая рамка плюс мягкая тень: без них белая карточка
        // растворялась в почти белом фоне.
        // Одна шкала полей для всех карточек: края содержимого в колонке совпадают.
        'card-glow border border-line bg-surface p-5 sm:p-6 lg:p-8',
        animate ? 'animate-slide-in motion-reduce:animate-none' : '',
        interactive
          ? 'press-card cursor-pointer hover:border-teal/40 hover:shadow-card-lg'
          : '',
        className ?? '',
      ].join(' ')}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {children}
    </Paper>
  );
}
